import { IconButton } from "@/components/common/button/IconButton";
import { useServices } from "@/hooks/api/services/useServices";
import { Choice, Message, MessageType } from "@/models/Message";
import { Avatar, Button, makeStyles, Spinner, Text, tokens } from "@fluentui/react-components";
import {
  BotRegular,
  bundleIcon,
  CopyFilled,
  CopyRegular,
  ThumbDislikeFilled,
  ThumbDislikeRegular,
  ThumbLikeFilled,
  ThumbLikeRegular,
} from "@fluentui/react-icons";
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { FeedbackDialog } from "../../feedback/FeedbackDialog";
import { ChoiceButtons } from "./ChoiceButtons";
import { DataSourceFAQ } from "./DataSourceFAQ";
import { DataSourceRAG } from "./DataSourceRAG";

const CopyIcon = bundleIcon(CopyFilled, CopyRegular);
const LikeIcon = bundleIcon(ThumbLikeFilled, ThumbLikeRegular);
const DislikeIcon = bundleIcon(ThumbDislikeFilled, ThumbDislikeRegular);

type Props = {
  message: Message;
  feedbackScore?: number;
  feedbackComment?: string;
  onFeedbackSelected?: (score: number) => void;
  onFeedbackCommentSubmit?: (comment: string) => void;
  onChoiceClick?: (choice: Choice) => void;
  onCopied: () => void;
};

export const AssistantMessageBubble: React.FC<Props> = ({
  message,
  feedbackScore,
  feedbackComment,
  onFeedbackSelected,
  onFeedbackCommentSubmit,
  onChoiceClick,
  onCopied,
}) => {
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { data: services } = useServices();
  const serviceNames = services!.nameMap();

  const formattedDatetime = new Date(message.createdAt).toLocaleString();

  // クリップボードにコピーするための処理
  const onCopyClick = () => {
    let dataSources: string[] = [];
    if (message.dataSources && message.type == MessageType.RAG) {
      dataSources = message.dataSources.map(ds => {
        const label = ds.title + (ds.section ? ":" + ds.section : "");
        return "" + label + " (" + ds.url + ")";
      });
    }
    if (message.dataSources && message.type == MessageType.FAQ) {
      dataSources = message.dataSources.map(ds => {
        return "[" + ds.section + "] " + ds.title;
      });
    }
    const content = message.content + (message.dataSources ? "\n参照元:\n" + dataSources.join("\n") : "");
    navigator.clipboard.writeText(content);
    onCopied();
  };

  const classes = useStyles();
  return (
    <div className={classes.wrapper}>
      <div className={classes.captionWrapper}>
        <Text className={classes.assistantName}>{serviceNames[message.sender]}</Text>
        <Text className={classes.datetime}>{formattedDatetime}</Text>
      </div>
      <div className={classes.messageWrapper}>
        <div className={classes.assistantIcon}>
          <Avatar icon={<BotRegular />} />
        </div>
        <div className={classes.message}>
          <ReactMarkdown
            components={{
              a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />,
            }}
          >
            {message.content}
          </ReactMarkdown>
          {message.type == MessageType.RAG && message.dataSources && (
            <DataSourceRAG
              dataSources={message.dataSources}
            />
          )}
          {message.type == MessageType.FAQ && message.dataSources && (
            <DataSourceFAQ
              dataSources={message.dataSources}
            />
          )}
          <div className={classes.actions}>
            <IconButton icon={<CopyIcon />} onClick={() => onCopyClick()} />
            {isFeedbackLoading ? <Spinner size="extra-tiny" /> : (
              feedbackScore && feedbackScore > 0 && !feedbackComment
                ? (
                  <Button
                    appearance="transparent"
                    className={classes.commentButton}
                    onClick={() => setIsDialogOpen(true)}
                  >
                    評価をコメント
                  </Button>
                )
                : (
                  <>
                    <IconButton
                      icon={<LikeIcon />}
                      isSelected={feedbackScore === 5}
                      onClick={() => {
                        setIsFeedbackLoading(true);
                        onFeedbackSelected?.(feedbackScore === 5 ? 0 : 5);
                        setTimeout(() => setIsFeedbackLoading(false), 500);
                      }}
                    />
                    <IconButton
                      icon={<DislikeIcon />}
                      isSelected={feedbackScore === 1}
                      onClick={() => {
                        setIsFeedbackLoading(true);
                        onFeedbackSelected?.(feedbackScore === 1 ? 0 : 1);
                        setTimeout(() => setIsFeedbackLoading(false), 500);
                      }}
                    />
                  </>
                )
            )}
          </div>
        </div>
      </div>

      {message.choices
        ? (
          <div className={classes.choices}>
            <ChoiceButtons choices={message.choices} onChoiceClick={onChoiceClick} />
          </div>
        )
        : <></>}
      <FeedbackDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={(comment: string) => {
          onFeedbackCommentSubmit?.(comment);
          setIsDialogOpen(false);
        }}
      />
    </div>
  );
};

const useStyles = makeStyles({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    maxWidth: "70%",
  },
  captionWrapper: {
    paddingLeft: "36px", // Assistantのアイコン+Gapの幅
    height: "20px",
    alignItems: "space-between",
    display: "flex",
    gap: tokens.spacingHorizontalXS,
  },
  assistantName: {
    fontSize: tokens.fontSizeBase200,
  },
  datetime: {
    fontSize: tokens.fontSizeBase200,
  },
  messageWrapper: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
  },
  assistantIcon: {
    width: "32px",
  },
  message: {
    background: tokens.colorNeutralBackground3,
    fontSize: tokens.fontSizeBase300,
    borderRadius: tokens.borderRadiusMedium,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  actions: {
    fontSize: tokens.fontSizeBase400,
    display: "flex",
    gap: tokens.spacingHorizontalS,
  },
  choices: {
    paddingLeft: "34px",
    paddingTop: tokens.spacingHorizontalS,
  },
  commentButton: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});
